import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { ChromeStatus } from '@urc-ui/utils/chrome-utils';
import { logger } from '@flexible-reports/services/logger';
import { ChromeStatusContext } from '@urc-ui/context/aac-chrome-status';
import { OutageBanner, PreOutageBanner } from '@urc-ui/components/outage-banner';
import { Section } from '@amzn/storm-ui';
import styled from 'styled-components';
import { isCaracalOutageBannerEnabled } from '@flexible-reports/utils/app-utils';
import { ErrorBanner } from '@flexible-reports/pages/shared/banner/error-banner';
import { useTranslation } from 'react-i18next';

const CHROME_COMPONENTS_INITIALIZED = 'CHROME_COMPONENTS_INITIALIZED';

const FullHeightSection = styled(Section)<{ hasSideNav: boolean; withMaxWidth: boolean }>`
  height: 100%;
  width: auto;
  justify-content: center;
  ${({ hasSideNav }) =>
    hasSideNav
      ? ''
      : 'margin-inline-start: -60px; padding-inline-start: 60px; // compensating for hardcoded in AAC Chrome #sc-content-container left margin that breaks pages with no side menu (https://t.corp.amazon.com/V1704758610)'}

  // Container added by Storm Section
  & > div {
    flex-direction: column;
    height: 100%;
    justify-content: flex-start;
    ${({ withMaxWidth }) => (withMaxWidth ? 'max-width: 100%;' : '')}
  }
`;

type LayoutProps = {
  hasSideNav?: boolean;
  withMaxWidthContainer?: boolean;
  isFluid?: boolean;
  withoutPadding?: boolean;
};

/**
 * The console team fires a custom event (CHROME_COMPONENTS_INITIALIZED) when they have initialized all the global
 * nav components and chrome API framework. See CCX CR https://code.amazon.com/reviews/CR-91935273/revisions/2#/details.
 * In our app, we block rendering until their component is fully loaded. We also want to gracefully degrade if their
 * app fails to load after 2s, hence the FORCED_READY state.
 */
export const Layout = ({ hasSideNav = false, withMaxWidthContainer = false, isFluid = false, withoutPadding = false }: LayoutProps) => {
  const [start] = useState(Date.now());
  const [chromeStatus, setChromeStatus] = useState(window.AACChrome?.hasInitialized ? ChromeStatus.READY : ChromeStatus.NOT_READY);
  const { t } = useTranslation();

  useEffect(() => {
    const chromeListener = () => {
      if (window.AACChrome?.hasInitialized) {
        const end = Date.now();
        logger.logInfoMessage(`Chrome loaded after ${(end - start) / 1000} seconds`);
        setChromeStatus(ChromeStatus.READY);
      }
    };

    window.addEventListener(CHROME_COMPONENTS_INITIALIZED, chromeListener);
    return () => window.removeEventListener(CHROME_COMPONENTS_INITIALIZED, chromeListener);
  }, [chromeStatus, start]);

  useEffect(() => {
    if (chromeStatus !== ChromeStatus.NOT_READY) {
      return;
    }

    const beacon = setTimeout(() => {
      setChromeStatus(ChromeStatus.FORCED_READY);
      logger.logErrorMessage('Failed to load Chrome after 2s');
    }, 2000);

    return () => clearTimeout(beacon);
  }, [chromeStatus]);

  if (window.AppContext?.isOutageEnabled === 'true') {
    return <OutageBanner end={window.AppContext.outageEndTime as string} />;
  }

  const isChromeReady = chromeStatus === ChromeStatus.FORCED_READY || chromeStatus === ChromeStatus.READY;
  return (
    <FullHeightSection
      id='application-container'
      data-testid='application-container'
      contentMaxWidth={isFluid ? 'fluid' : 'large'}
      padding={withoutPadding ? 'none' : 'base'}
      hasSideNav={hasSideNav}
      withMaxWidth={withMaxWidthContainer}
    >
      <ChromeStatusContext.Provider value={isChromeReady}>
        {isCaracalOutageBannerEnabled() && (
          <ErrorBanner
            header=''
            message={t(
              'flexible_reports_outage_banner_text',
              "We’re having some issues with reporting and are working to fix them. We're sorry for any inconvenience."
            )}
          />
        )}
        {window.AppContext?.isPreOutageEnabled === 'true' ? (
          <PreOutageBanner start={window.AppContext.outageStartTime as string} end={window.AppContext.outageEndTime as string} />
        ) : null}
        <Outlet />
      </ChromeStatusContext.Provider>
    </FullHeightSection>
  );
};
